import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  Container,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import { DEFAULT_ROWS_PER_PAGE } from "components/LazyTable";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import {
  ContentTypeFilter,
  IMediaItem,
  ListRequestOfContentTypeFilter,
  MediaItemSource,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import {
  MediaFilterType,
  getContentTypesForFilters,
  matchesFilter,
  allMediaLibraryTypes,
} from "utils/mediaUtils";
import MediaLibraryListItem from "./MediaLibraryListItem";

const itemCountForBiggerSpacing = 3;

type MediaLibraryItemsListViewProps = {
  source?: MediaItemSource;
  options?: MediaFilterType[];
  onClickCreateMediaLibraryItem?: () => void;
  getItemLabel?: (mediaLibraryItem: IMediaItem) => string;
  onClickMediaLibraryItem: (mediaLibraryItem: IMediaItem) => void;
};

const MediaLibraryItemsListView = ({
  source = MediaItemSource.MediaLibrary,
  options,
  onClickCreateMediaLibraryItem,
  onClickMediaLibraryItem,
  getItemLabel,
}: MediaLibraryItemsListViewProps) => {
  const { formatMessage: t } = useIntl();
  const { mediaService, createMediaLibraryItemUrl } = useGlobalContext();
  const { organizationId } = contextStore.useState();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [
    contentTypeFilterValue,
    setContentTypeFilterValue,
  ] = useState<MediaFilterType | null>(null);

  const searchTerm = persistanceStore.useState(
    (s) => s.mediaLibraryFilterSearchTerm
  );
  const debouncedSearchTerm = useDebounce(searchTerm?.trim(), 500);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, contentTypeFilterValue]);

  const { data, isFetching } = useStrictQuery(
    [
      "media-items",
      source,
      organizationId,
      page,
      rowsPerPage,
      debouncedSearchTerm,
      contentTypeFilterValue,
      options,
    ],
    () =>
      mediaService.getItems(
        source,
        ListRequestOfContentTypeFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: "createdOn",
          sortDescending: true,
          parameters: ContentTypeFilter.fromJS({
            searchText: debouncedSearchTerm,
            contentType: getContentTypesForFilters(
              contentTypeFilterValue ? [contentTypeFilterValue] : options ?? []
            ).join(", "),
          }),
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  const mediaLibraryItems = data?.data as IMediaItem[];

  const noResults = (data?.totalRecords ?? 0) === 0 && !isFetching;

  const filteredMediaLibraryItems = useMemo(
    () =>
      mediaLibraryItems?.filter(
        (mli) =>
          !contentTypeFilterValue ||
          matchesFilter(mli.contentType!, [contentTypeFilterValue])
      ),
    [mediaLibraryItems, contentTypeFilterValue]
  );

  const fileFormats = options ?? allMediaLibraryTypes;
  const dispayFormatsFilter = fileFormats.length > 1;

  return (
    <Container>
      <Box mb={3} textAlign="center">
        {onClickCreateMediaLibraryItem ? (
          <Fab
            aria-label="add"
            color="primary"
            onClick={onClickCreateMediaLibraryItem}
          >
            <AddIcon />
          </Fab>
        ) : (
          <Link
            to={createMediaLibraryItemUrl()}
            style={{ textDecoration: "none" }}
          >
            <Fab aria-label="add" color="primary">
              <AddIcon />
            </Fab>
          </Link>
        )}
      </Box>

      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item sm={12} md={dispayFormatsFilter ? 8 : 12}>
            <TextField
              fullWidth
              variant="outlined"
              value={searchTerm ?? ""}
              placeholder={t({ id: "searchPlaceholder" })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        persistanceStore.update((s) => {
                          s.mediaLibraryFilterSearchTerm = "";
                        })
                      }
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e) =>
                persistanceStore.update((s) => {
                  s.mediaLibraryFilterSearchTerm = e.target.value;
                })
              }
            />
          </Grid>
          {dispayFormatsFilter && (
            <Grid item sm={12} md={4}>
              <Autocomplete
                id="fileFormat"
                options={fileFormats}
                onChange={(_, value) => setContentTypeFilterValue(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t({ id: "fileFormatLabel" })}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      </Box>

      {noResults && (
        <Box textAlign="center" my={2}>
          <Typography>
            <T id="noResults" />
          </Typography>
        </Box>
      )}

      {!isFetching && filteredMediaLibraryItems ? (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "20px",
            justifyContent:
              filteredMediaLibraryItems.length > itemCountForBiggerSpacing
                ? "space-between"
                : "flex-start",
          }}
        >
          {filteredMediaLibraryItems.map((mediaLibraryItem) => (
            <MediaLibraryListItem
              key={mediaLibraryItem.id}
              mediaLibraryItem={mediaLibraryItem}
              onClickMediaLibraryItem={onClickMediaLibraryItem}
              getItemLabel={getItemLabel}
            />
          ))}
        </Box>
      ) : (
        <LinearProgress style={{ marginBottom: "10px" }} />
      )}

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
        component="div"
        count={data?.totalRecords ?? 0}
        rowsPerPage={rowsPerPage}
        page={page}
        labelRowsPerPage={t({ id: "mediaPerPage" })}
        onPageChange={(e: any, newPage: number) => setPage(newPage)}
        onRowsPerPageChange={(e: any) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Container>
  );
};

export default withErrorBoundary(MediaLibraryItemsListView);
